import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { RecentUserEntry } from '@app/core/layout/recent-users.service';

export interface UserQuickSwitchBottomSheetData {
  users: RecentUserEntry[];
  currentUserId: string | null;
}

/**
 * User Quick Switch Bottom Sheet Component
 * M3 Bottom Sheet pattern for mobile user switching.
 */
@Component({
  selector: 'app-user-quick-switch-bottom-sheet',
  templateUrl: './user-quick-switch-bottom-sheet.component.html',
  styleUrls: ['./user-quick-switch-bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS],
})
export class UserQuickSwitchBottomSheetComponent {
  private readonly bottomSheetRef = inject(MatBottomSheetRef<UserQuickSwitchBottomSheetComponent>);
  readonly data = inject<UserQuickSwitchBottomSheetData>(MAT_BOTTOM_SHEET_DATA);

  /** Search query for filtering users */
  readonly searchQuery = signal('');

  /** Filtered users based on search query */
  readonly filteredUsers = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const currentId = this.data.currentUserId;
    let users = this.data.users;

    // Exclude current user
    if (currentId) {
      users = users.filter(u => u.id !== currentId);
    }

    // Filter by search query
    if (query) {
      users = users.filter(u => u.name.toLowerCase().includes(query));
    }

    return users;
  });

  /** Select a user and close the sheet */
  selectUser(user: RecentUserEntry): void {
    this.bottomSheetRef.dismiss(user);
  }

  /** Close the sheet without selection */
  close(): void {
    this.bottomSheetRef.dismiss();
  }

  /** Update search query */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  /** Get initials for avatar */
  getInitials(name: string): string {
    if (!name) return '';
    return name.split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase();
  }
}
